// src/components/layout/Sidebar.jsx

import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  MapPin,
  PlayCircle,
  Fuel,
  Ship,
  Building2,
  Users,
  FileText,
  Settings,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  PlusCircle,
  Menu,
  Cpu,
} from 'lucide-react';

import { useStore } from '../../store/useStore';

const Sidebar = () => {
  const {
    darkMode,
    toggleDarkMode,
    sidebarCollapsed,
    toggleSidebar,
    user,
  } = useStore();

  const [hoveredItem, setHoveredItem] = useState(null);

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, name: 'Dashboard', roles: ['all'] },
    { path: '/map', icon: MapPin, name: 'Live Tracking', roles: ['all'] },
    { path: '/playback', icon: PlayCircle, name: 'Trip Playback', roles: ['all'] },
    { path: '/fuel', icon: Fuel, name: 'Fuel Monitor', roles: ['all'] },
    { path: '/sensors', icon: Cpu, name: 'Sensor Monitor', roles: ['all'] },
    { path: '/vessels', icon: Ship, name: 'Vessels', roles: ['all'] },
    { path: '/vessels/register', icon: PlusCircle, name: 'Register Vessel', roles: ['company_admin', 'super_admin'] },
    { path: '/companies', icon: Building2, name: 'Companies', roles: ['super_admin'] },
    { path: '/users', icon: Users, name: 'Users', roles: ['super_admin', 'company_admin'] },
    { path: '/reports', icon: FileText, name: 'Reports', roles: ['all'] },
    { path: '/settings', icon: Settings, name: 'Settings', roles: ['all'] },
  ];

  const filteredMenu = menuItems.filter(
    (item) =>
      item.roles.includes('all') ||
      (user && item.roles.includes(user.role))
  );

  return (
    <>
      {/* Mobile Button */}
      <button
        onClick={toggleSidebar}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 h-screen z-40
          bg-white dark:bg-surface-900
          border-r border-surface-200 dark:border-surface-800
          transition-all duration-300 ease-in-out
          ${sidebarCollapsed ? 'w-[68px]' : 'w-[240px]'}
          ${sidebarCollapsed ? 'lg:translate-x-0 -translate-x-full' : 'translate-x-0'}
        `}
      >
        {/* Header */}
        <div
          className={`
            h-14 border-b border-surface-200 dark:border-surface-800
            flex items-center
            ${sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-4'}
          `}
        >
          <div
            className={`flex items-center ${
              sidebarCollapsed ? 'justify-center' : 'gap-2'
            }`}
          >
            <div className="w-8 h-8 bg-primary-600 flex items-center justify-center shrink-0">
              <span className="text-white text-xs font-bold">FM</span>
            </div>

            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="overflow-hidden"
                >
                  <h1 className="text-sm font-semibold text-surface-900 dark:text-white leading-none">
                    FuelMonitor
                  </h1>
                  <p className="text-[10px] text-surface-500 dark:text-surface-400 mt-0.5">
                    Monitoring
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {!sidebarCollapsed && (
            <button
              onClick={toggleSidebar}
              className="hidden lg:flex items-center justify-center w-7 h-7 hover:bg-surface-100 dark:hover:bg-surface-800 transition"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}

          {sidebarCollapsed && (
            <button
              onClick={toggleSidebar}
              className="hidden lg:flex absolute -right-2 top-5 items-center justify-center w-5 h-5 bg-white dark:bg-surface-900 border border-surface-300 dark:border-surface-700"
            >
              <ChevronRight className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="p-2 space-y-1 overflow-y-auto h-[calc(100vh-112px)]">
          {filteredMenu.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onMouseEnter={() => setHoveredItem(item.path)}
              onMouseLeave={() => setHoveredItem(null)}
              className={({ isActive }) =>
                `
                relative flex items-center
                transition-all duration-150
                ${
                  sidebarCollapsed
                    ? 'justify-center h-10 w-10 mx-auto'
                    : 'gap-3 px-3 h-10'
                }
                ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-surface-600 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-surface-900 dark:hover:text-white'
                }
              `
              }
            >
              <item.icon className="w-[18px] h-[18px] shrink-0" />

              <AnimatePresence>
                {!sidebarCollapsed && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-[13px] font-medium whitespace-nowrap"
                  >
                    {item.name}
                  </motion.span>
                )}
              </AnimatePresence>

              {/* Tooltip */}
              {sidebarCollapsed && hoveredItem === item.path && (
                <div className="absolute left-14 px-2 py-1 bg-surface-900 text-white text-[11px] whitespace-nowrap z-50 shadow-lg">
                  {item.name}
                </div>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-surface-200 dark:border-surface-800 p-2 bg-white dark:bg-surface-900">
          <button
            onClick={toggleDarkMode}
            className={`
              flex items-center transition-all duration-150
              text-surface-600 dark:text-surface-400
              hover:bg-surface-100 dark:hover:bg-surface-800
              ${
                sidebarCollapsed
                  ? 'justify-center h-10 w-10 mx-auto'
                  : 'gap-3 px-3 h-10 w-full'
              }
            `}
          >
            {darkMode ? (
              <Sun className="w-[18px] h-[18px]" />
            ) : (
              <Moon className="w-[18px] h-[18px]" />
            )}

            {!sidebarCollapsed && (
              <span className="text-[13px] font-medium">
                Theme
              </span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;